const envBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '');
const isDev = import.meta.env.DEV;
const API_BASE_URL = envBaseUrl || (isDev ? 'http://localhost:3000' : '');

if (isDev && !envBaseUrl) {
  console.info('VITE_API_BASE_URL is not set; using http://localhost:3000 for API calls in development.');
} else if (!isDev && !envBaseUrl) {
  console.warn('VITE_API_BASE_URL is not set in production. The app will use relative /api paths.');
}

const safeJson = async (res) => {
  const text = await res.text();
  if (!text) return { success: false, error: 'Empty response' };

  try {
    return JSON.parse(text);
  } catch (err) {
    return { success: false, error: `Invalid JSON response: ${err.message}` };
  }
};

const apiFetch = async (path, options) => {
  const url = `${API_BASE_URL}${path}`;
  try {
    const res = await fetch(url, options);
    if (!res.ok) {
      const text = await res.text();
      console.error(`API request failed: ${url} ${res.status} ${res.statusText} - ${text}`);
      return { ok: false, status: res.status, statusText: res.statusText, text: async () => text };
    }
    return res;
  } catch (err) {
    console.error(`API request failed: ${url} - ${err.message}`);
    return {
      ok: false,
      status: 0,
      statusText: err.message,
      text: async () => JSON.stringify({ success: false, error: err.message }),
    };
  }
};

const postJson = async (path, data) => {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return safeJson(res);
};

const putJson = async (path, data) => {
  const res = await apiFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return safeJson(res);
};

export const api = {
  auth: {
    login: async (email, password) => postJson('/api/auth/login', { email, password }),
    register: async (email, password) => postJson('/api/auth/register', { email, password })
  },

  users: {
    getAll: async () => {
      const res = await apiFetch('/api/users');
      return safeJson(res);
    },
    getByEmail: async (email) => {
      const res = await apiFetch(`/api/users/${encodeURIComponent(email)}`);
      return safeJson(res);
    },
    create: async (user) => postJson('/api/users', user),
    update: async (id, payload) => putJson(`/api/users/${id}`, payload),
    delete: async (id) => {
      const res = await apiFetch(`/api/users/${id}`, { method: 'DELETE' });
      return safeJson(res);
    }
  },

  servers: {
    getAll: async () => {
      const res = await apiFetch('/api/servers');
      return safeJson(res);
    },
    add: async (server) => postJson('/api/servers', server),
    update: async (id, payload) => putJson(`/api/servers/${id}`, payload),
    delete: async (id) => {
      const res = await apiFetch(`/api/servers/${id}`, { method: 'DELETE' });
      return safeJson(res);
    }
  },

  widgets: {
    getAll: async () => {
      const res = await apiFetch('/api/widgets');
      return safeJson(res);
    },
    add: async (widget) => postJson('/api/widgets', widget),
    update: async (id, payload) => putJson(`/api/widgets/${id}`, payload),
    delete: async (id) => {
      const res = await apiFetch(`/api/widgets/${id}`, { method: 'DELETE' });
      return safeJson(res);
    },
    updateLayout: async (layout) => putJson('/api/widgets/layout', { layout })
  }
};